import React, { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";

// Fotos reales del centro de Madrid
const PHOTOS = [
  { src: "https://customer-assets.emergentagent.com/job_optometry-hub-4/artifacts/kk0c0cxx_Fachada.jpg", alt: "Fachada del centro Salud y Visión en Madrid", caption: "Nuestra fachada — C/ Alcalde López Casero 10" },
  { src: "https://customer-assets.emergentagent.com/job_optometry-hub-4/artifacts/ycb4agwy_Sala%20TV.jpg", alt: "Sala de terapia visual y espera", caption: "Sala de terapia visual" },
  { src: "https://customer-assets.emergentagent.com/job_optometry-hub-4/artifacts/odwl1ysf_entrada.jpg", alt: "Entrada interior del centro", caption: "Entrada del centro" },
  { src: "https://customer-assets.emergentagent.com/job_optometry-hub-4/artifacts/kdpr1l0z_Mostrador.jpg", alt: "Mostrador de recepción", caption: "Mostrador y recepción" },
  { src: "https://customer-assets.emergentagent.com/job_optometry-hub-4/artifacts/qipuddwa_Gabinete.jpg", alt: "Gabinete optométrico principal", caption: "Gabinete optométrico" },
  { src: "https://customer-assets.emergentagent.com/job_optometry-hub-4/artifacts/djxnvdh9_Gabinete%20op.jpg", alt: "Gabinete de optometría avanzada", caption: "Gabinete de optometría avanzada" },
  { src: "https://customer-assets.emergentagent.com/job_optometry-hub-4/artifacts/fn82d76v_copy_1C8BFC27-9FCA-4049-9668-42631348514B.JPEG", alt: "Consulta personalizada", caption: "Consulta personalizada" },
  { src: "https://customer-assets.emergentagent.com/job_optometry-hub-4/artifacts/kt8i59kq_662C0F79-4AEE-480C-9EA6-93E1BB5F2AF6.jpeg", alt: "Área de terapia visual", caption: "Área de terapia visual" },
];

const MadridGallery = () => {
  const [index, setIndex] = useState(-1); // -1 = cerrado

  const open = (i) => setIndex(i);
  const close = useCallback(() => setIndex(-1), []);
  const next = useCallback(() => setIndex((i) => (i + 1) % PHOTOS.length), []);
  const prev = useCallback(() => setIndex((i) => (i - 1 + PHOTOS.length) % PHOTOS.length), []);

  // Teclado + scroll lock cuando el lightbox está abierto
  useEffect(() => {
    if (index === -1) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [index, close, next, prev]);

  return (
    <>
      {/* Mosaico — Fachada 2x2 y resto 1x1, 4 columnas en desktop */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 [grid-auto-rows:1fr]" data-testid="madrid-gallery">
        {PHOTOS.map((p, i) => {
          const featured = i === 0; // Fachada 2x2
          const wide = i === PHOTOS.length - 1; // Última foto ocupa 2 columnas para cerrar el mosaico
          return (
            <button
              key={i}
              onClick={() => open(i)}
              className={`group relative rounded-2xl md:rounded-3xl overflow-hidden cursor-pointer ${featured ? "col-span-2 row-span-2" : ""} ${wide ? "md:col-span-2" : ""}`}
              style={{
                border: "1px solid var(--sage-100)",
                boxShadow: "0 15px 30px -20px rgba(54,66,32,0.25)",
                aspectRatio: featured ? "1/1" : wide ? "2/1" : "1/1",
              }}
              data-testid={`madrid-photo-${i}`}
              aria-label={`Ver foto: ${p.alt}`}
            >
              <img
                src={p.src}
                alt={p.alt}
                loading={i > 2 ? "lazy" : "eager"}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                style={{ filter: "saturate(1.05) contrast(1.03)" }}
              />
              {/* Overlay con caption */}
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-between p-3 md:p-5"
                style={{ background: "linear-gradient(180deg, rgba(54,66,32,0) 40%, rgba(54,66,32,0.75) 100%)" }}
              >
                <span className="font-sans-body text-xs md:text-sm font-medium text-left" style={{ color: "var(--cream)", textShadow: "0 2px 8px rgba(0,0,0,0.4)" }}>
                  {p.caption}
                </span>
                <span className="h-7 w-7 md:h-8 md:w-8 rounded-full flex items-center justify-center shrink-0 ml-2" style={{ background: "rgba(250,247,238,0.92)" }}>
                  <Maximize2 size={14} style={{ color: "var(--sage-700)" }} />
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Pista de navegación por teclado */}
      <div className="mt-4 flex items-center gap-2 font-sans-body text-xs text-[color:var(--ink-soft)]">
        <Maximize2 size={14} /> Toca cualquier foto para verla a pantalla completa
      </div>

      {/* ==== LIGHTBOX ==== */}
      {index !== -1 && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 md:p-8"
          style={{ background: "rgba(15,20,10,0.92)", backdropFilter: "blur(12px)" }}
          onClick={close}
          data-testid="lightbox-root"
        >
          {/* Cerrar */}
          <button
            onClick={(e) => { e.stopPropagation(); close(); }}
            aria-label="Cerrar galería"
            className="absolute top-4 right-4 md:top-6 md:right-6 h-11 w-11 rounded-full flex items-center justify-center transition-transform hover:scale-110"
            style={{ background: "rgba(250,247,238,0.12)", color: "var(--cream)", backdropFilter: "blur(8px)" }}
            data-testid="lightbox-close"
          >
            <X size={22} />
          </button>

          {/* Contador */}
          <div className="absolute top-6 left-1/2 -translate-x-1/2 font-sans-body text-sm" style={{ color: "var(--cream)", opacity: 0.85 }}>
            {index + 1} / {PHOTOS.length}
          </div>

          {/* Anterior */}
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Foto anterior"
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full flex items-center justify-center transition-transform hover:scale-110"
            style={{ background: "rgba(250,247,238,0.12)", color: "var(--cream)", backdropFilter: "blur(8px)" }}
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={26} />
          </button>

          {/* Siguiente */}
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Foto siguiente"
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full flex items-center justify-center transition-transform hover:scale-110"
            style={{ background: "rgba(250,247,238,0.12)", color: "var(--cream)", backdropFilter: "blur(8px)" }}
            data-testid="lightbox-next"
          >
            <ChevronRight size={26} />
          </button>

          {/* Imagen */}
          <figure
            className="relative max-w-[92vw] max-h-[85vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              key={PHOTOS[index].src}
              src={PHOTOS[index].src}
              alt={PHOTOS[index].alt}
              className="max-w-full max-h-[78vh] object-contain rounded-xl"
              style={{ boxShadow: "0 40px 80px -20px rgba(0,0,0,0.5)", animation: "fadeIn 0.25s ease-out" }}
              data-testid="lightbox-image"
            />
            <figcaption
              className="mt-4 font-sans-body text-sm md:text-base text-center"
              style={{ color: "var(--cream)", textShadow: "0 2px 8px rgba(0,0,0,0.5)" }}
            >
              {PHOTOS[index].caption}
            </figcaption>
          </figure>

          <style>{`
            @keyframes fadeIn { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: scale(1); } }
          `}</style>
        </div>
      )}
    </>
  );
};

export default MadridGallery;
